// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { useParams } from 'react-router-dom';
import LoadingSpinner from 'Components/LoadingSpinner';
import { CSVLink } from 'react-csv';
import exportIcon from 'Assets/export.png';
import orderListIcon from 'Assets/order_list_icon.png';
import { RootState } from 'Store';
import moment, { Moment } from 'moment-timezone';
import { GetAccountCreditLedgerThunk } from 'Features/account/accountThunk';
import { GetOrgThunk } from '../adminThunk';
import { SetOrgIdAction } from '../adminActions';
import stylesDef from './styles';

const DatePickerArrowIcon = () => (
  <Box component="span" sx={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.54)' }}>▾</Box>
);

const Ledger = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { orgId } = useParams();

  const { accountCreditLedger } = useAppSelector((state: RootState) => state.account);
  const [showLoader, setShowLoader] = useState(false);
  const [periodType, setPeriodType] = useState<AccountCreditLedgerPeriod>('all');
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [customStartDate, setCustomStartDate] = useState<Moment | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Moment | null>(null);
  const [earliestTransactionDate, setEarliestTransactionDate] = useState<Moment | null>(null);

  // Generate available years (current year down to earliest transaction year)
  const availableYears = useMemo(() => {
    const years = [];
    const currentYear = new Date().getFullYear();
    const earliestYear = earliestTransactionDate?.year() ?? currentYear;
    for (let year = currentYear; year >= earliestYear; year -= 1) {
      years.push(year);
    }
    return years;
  }, [earliestTransactionDate]);

  // Generate available months (from current month back to earliest transaction month)
  const availableMonths = useMemo(() => {
    const months = [];
    const now = moment();
    const earliest = earliestTransactionDate?.clone().startOf('month') ?? now.clone().startOf('month');
    let current = now.clone().startOf('month');

    while (current.isSameOrAfter(earliest)) {
      months.push({
        value: `${current.year()}-${current.month()}`,
        month: current.month(),
        year: current.year(),
        label: current.format('MMMM YYYY'),
      });
      current = current.subtract(1, 'month');
    }
    return months;
  }, [earliestTransactionDate]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadLedgerData();
  }, [periodType, selectedMonth, selectedYear, customStartDate, customEndDate]);

  const loadData = async () => {
    setShowLoader(true);
    if (_ADMIN_) {
      await Promise.all([
        dispatch(GetOrgThunk(orgId)),
        dispatch(SetOrgIdAction(orgId)),
      ]);
    }
    // Initial load with 'all' period to get the earliest transaction date
    const result = await dispatch(GetAccountCreditLedgerThunk(orgId, { period: 'all' }));
    if (result?.success && result.data?.data?.summary?.openingDate) {
      setEarliestTransactionDate(moment(result.data.data.summary.openingDate));
    }
    setShowLoader(false);
  };

  const loadLedgerData = async () => {
    const filterOptions: {
      period?: AccountCreditLedgerPeriod;
      month?: number;
      year?: number;
      startDate?: string;
      endDate?: string;
    } = { period: periodType };

    if (periodType === 'specificMonth') {
      filterOptions.month = selectedMonth;
      filterOptions.year = selectedYear;
    } else if (periodType === 'specificYear') {
      filterOptions.year = selectedYear;
    } else if (periodType === 'customRange' && customStartDate && customEndDate) {
      filterOptions.startDate = customStartDate.format('YYYY-MM-DD');
      filterOptions.endDate = customEndDate.format('YYYY-MM-DD');
    }

    await dispatch(GetAccountCreditLedgerThunk(orgId, filterOptions));
  };

  const handlePeriodChange = (event: SelectChangeEvent<AccountCreditLedgerPeriod>) => {
    setPeriodType(event.target.value as AccountCreditLedgerPeriod);
  };

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    const [year, month] = event.target.value.split('-');
    setSelectedYear(parseInt(year, 10));
    setSelectedMonth(parseInt(month, 10));
  };

  const handleYearChange = (event: SelectChangeEvent<string>) => {
    setSelectedYear(parseInt(event.target.value, 10));
  };

  const handleStartDateChange = (date: Moment | null) => {
    setCustomStartDate(date);
  };

  const handleEndDateChange = (date: Moment | null) => {
    setCustomEndDate(date);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return moment(dateString).format('MMM D, YYYY');
  };

  const formatCurrency = (amount: number) => amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formatAmount = (amount: number, type: 'credit' | 'debit') => {
    const formatted = formatCurrency(Math.abs(amount));
    if (type === 'credit') {
      return `+${formatted}`;
    }
    return `-${formatted}`;
  };

  // CSV data for export
  const csvData = useMemo(() => {
    if (!accountCreditLedger?.transactions) return [];

    const rows: any[] = [];

    // Add opening balance row
    if (accountCreditLedger.summary.openingDate) {
      rows.push({
        date: formatDate(accountCreditLedger.summary.openingDate),
        reference: '-',
        description: 'Opening Balance',
        creditDebit: '-',
        balance: formatCurrency(accountCreditLedger.summary.openingBalance),
      });
    }

    // Add transaction rows
    accountCreditLedger.transactions.forEach((txn) => {
      rows.push({
        date: formatDate(txn.date),
        reference: txn.reference,
        description: txn.description,
        creditDebit: formatAmount(txn.amount, txn.type),
        balance: formatCurrency(txn.balance),
      });
    });

    // Add closing balance row
    if (accountCreditLedger.summary.closingDate) {
      rows.push({
        date: formatDate(accountCreditLedger.summary.closingDate),
        reference: '-',
        description: 'Closing Balance',
        creditDebit: '-',
        balance: formatCurrency(accountCreditLedger.summary.closingBalance),
      });
    }

    return rows;
  }, [accountCreditLedger]);

  const csvHeaders = [
    { label: 'Date', key: 'date' },
    { label: 'Reference', key: 'reference' },
    { label: 'Description', key: 'description' },
    { label: 'Credit/Debit', key: 'creditDebit' },
    { label: 'Balance', key: 'balance' },
  ];

  return (
    <LoadingSpinner loading={showLoader}>
      <Box sx={styles.pageWrapper}>
        <Box sx={styles.headerSection}>
          <Typography variant="h2">Account Credit Ledger</Typography>
          <Box sx={styles.filterSection}>
            <Typography variant="body2" sx={styles.filterLabel}>Filter by Period:</Typography>
            <FormControl size="small" sx={styles.periodSelect}>
              <Select
                value={periodType}
                onChange={handlePeriodChange}
                displayEmpty
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="last30Days">Last 30 Days</MenuItem>
                <MenuItem value="last90Days">Last 90 Days</MenuItem>
                <MenuItem value="specificMonth">Specific Month</MenuItem>
                <MenuItem value="specificYear">Specific Year</MenuItem>
                <MenuItem value="customRange">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {periodType === 'specificMonth' && (
              <FormControl size="small" sx={styles.monthSelect}>
                <Select
                  value={`${selectedYear}-${selectedMonth}`}
                  onChange={handleMonthChange}
                  MenuProps={styles.selectMenuProps}
                >
                  {availableMonths.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {periodType === 'specificYear' && (
              <FormControl size="small" sx={styles.yearSelect}>
                <Select
                  value={`${selectedYear}`}
                  onChange={handleYearChange}
                  MenuProps={styles.selectMenuProps}
                >
                  {availableYears.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {periodType === 'customRange' && (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Box sx={styles.dateRangeInputs}>
                  <DatePicker
                    value={customStartDate}
                    onChange={handleStartDateChange}
                    format="MM/DD/YYYY"
                    slots={{ openPickerIcon: DatePickerArrowIcon }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        placeholder: 'From',
                        sx: styles.datePicker,
                      },
                    }}
                  />
                  <DatePicker
                    value={customEndDate}
                    onChange={handleEndDateChange}
                    format="MM/DD/YYYY"
                    slots={{ openPickerIcon: DatePickerArrowIcon }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        placeholder: 'Until',
                        sx: styles.datePicker,
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            )}
          </Box>
        </Box>

        <Box sx={styles.summaryCardsContainer}>
          <Box sx={styles.summaryCard}>
            <Typography variant="h3" sx={styles.summaryValue}>
              {formatCurrency(accountCreditLedger?.summary?.openingBalance || 0)}
            </Typography>
            <Typography variant="body2" sx={styles.summaryLabel}>
              Opening Credit Balance
              {accountCreditLedger?.summary?.openingDate && (
                <span>
                  {` (as of ${formatDate(accountCreditLedger.summary.openingDate)})`}
                </span>
              )}
            </Typography>
          </Box>

          <Box sx={styles.summaryCard}>
            <Box sx={styles.periodActivityValues}>
              <Typography variant="h3" sx={styles.creditValue}>
                {`+${formatCurrency(accountCreditLedger?.summary?.totalCredits || 0)}`}
              </Typography>
              <Typography variant="h3" sx={styles.debitValue}>
                {`-${formatCurrency(accountCreditLedger?.summary?.totalDebits || 0)}`}
              </Typography>
            </Box>
            <Typography variant="body2" sx={styles.summaryLabel}>
              Period Activity (Credits/Debits)
            </Typography>
          </Box>

          <Box sx={styles.summaryCard}>
            <Typography variant="h3" sx={styles.summaryValue}>
              {formatCurrency(accountCreditLedger?.summary?.closingBalance || 0)}
            </Typography>
            <Typography variant="body2" sx={styles.summaryLabel}>
              Closing Balance
              {accountCreditLedger?.summary?.closingDate && (
                <span>{` (as of ${formatDate(accountCreditLedger.summary.closingDate)})`}</span>
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.transactionSection}>
          <Box sx={styles.transactionHeader}>
            <Typography variant="h4">Transaction History</Typography>
            {accountCreditLedger?.transactions?.length > 0 && (
              <CSVLink
                data={csvData}
                headers={csvHeaders}
                filename={`account_credit_ledger_${new Date().toISOString().split('T')[0]}.csv`}
                style={styles.exportButton as React.CSSProperties}
                onClick={() => {
                  window.pendo?.track('Export Account Credit Ledger');
                }}
              >
                <img src={exportIcon} alt="export" style={{ width: 16, height: 16 }} />
                <Typography variant="body2" sx={styles.exportButtonText}>EXPORT</Typography>
              </CSVLink>
            )}
          </Box>

          {!showLoader && accountCreditLedger?.transactions?.length > 0 ? (
            <TableContainer component={Paper} sx={styles.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.tableHeaderCell}>DATE</TableCell>
                    <TableCell sx={styles.tableHeaderCell}>REFERENCE</TableCell>
                    <TableCell sx={styles.tableHeaderCell}>DESCRIPTION</TableCell>
                    <TableCell sx={styles.tableHeaderCell} align="right">CREDIT/DEBIT</TableCell>
                    <TableCell sx={styles.tableHeaderCell} align="right">BALANCE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Opening Balance Row */}
                  {accountCreditLedger.summary.openingDate && (
                    <TableRow>
                      <TableCell sx={styles.tableCellBold}>
                        {formatDate(accountCreditLedger.summary.openingDate)}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>-</TableCell>
                      <TableCell sx={styles.tableCellBold}>Opening Balance</TableCell>
                      <TableCell sx={styles.tableCell} align="right">-</TableCell>
                      <TableCell sx={styles.tableCell} align="right">
                        {formatCurrency(accountCreditLedger.summary.openingBalance)}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Transaction Rows */}
                  {accountCreditLedger.transactions.map((txn) => (
                    <TableRow key={txn._id}>
                      <TableCell sx={styles.tableCell}>{formatDate(txn.date)}</TableCell>
                      <TableCell sx={styles.tableCell}>{txn.reference}</TableCell>
                      <TableCell sx={styles.tableCell}>{txn.description}</TableCell>
                      <TableCell
                        sx={txn.type === 'credit' ? styles.creditCell : styles.debitCell}
                        align="right"
                      >
                        {formatAmount(txn.amount, txn.type)}
                      </TableCell>
                      <TableCell sx={styles.tableCell} align="right">
                        {formatCurrency(txn.balance)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Closing Balance Row */}
                  {accountCreditLedger.summary.closingDate && (
                    <TableRow>
                      <TableCell sx={styles.tableCellBold}>
                        {formatDate(accountCreditLedger.summary.closingDate)}
                      </TableCell>
                      <TableCell sx={styles.tableCell}>-</TableCell>
                      <TableCell sx={styles.tableCellBold}>Closing Balance</TableCell>
                      <TableCell sx={styles.tableCell} align="right">-</TableCell>
                      <TableCell sx={styles.tableCellBold} align="right">
                        {formatCurrency(accountCreditLedger.summary.closingBalance)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            !showLoader && (
              <Box sx={styles.noDataContainer}>
                <img src={orderListIcon} alt="icon" />
                <Typography variant="h5">There is no credit history to show.</Typography>
              </Box>
            )
          )}
        </Box>
      </Box>
    </LoadingSpinner>
  );
};

export default Ledger;
